import { userInfoAtom } from 'app/atoms';
import { withErrorBoundary } from 'app/components/error-boundary';
import Responsive from 'app/components/responsive';
import { images } from 'app/design/images';
import { Column, Row } from 'app/design/primitives/box';
import { PCImage } from 'app/design/primitives/image';
import { Page } from 'app/design/primitives/page';
import { Text } from 'app/design/primitives/text';
import { Separator } from 'app/design/primitives/text-divider';

import { useNavigationOptions } from 'app/hooks/navigation';
import { useDeviceWidth } from 'app/hooks/use-device-size';
import { withDashboardLayout } from 'app/hooks/with-dashboard-layout';
import { useStatistics } from 'app/network/statistics';
import { useUser } from 'app/network/user';
import { useOnFocus } from 'app/services/on-focus';
import type { NavigationScreen } from 'app/types/navigation';
import { WhiteLabel } from 'app/types/white-labels';
import { isWhiteLabel } from 'app/utils/helpers';
import { useUISize, useAlignmentIfDesktop, usePageTitle } from 'app/utils/web-helpers';
import { useAtomValue } from 'jotai/index';
import React, { useEffect } from 'react';
import { withOptionalMetrics } from '../../utils/performance/with-metrics';
import { useRegisterLoading } from 'app/utils/performance/screen-perf-context';
import { Trans, useTranslation } from 'react-i18next';
import { Pressable } from 'react-native';
import ConfettiCannon from 'react-native-confetti-cannon';
import type { StyleProp } from 'react-native/Libraries/StyleSheet/StyleSheet';
import type { ViewStyle } from 'react-native/Libraries/StyleSheet/StyleSheetTypes';
import { SuperPartagerDetails } from './super-partageur-details';
import { createTheme as createDSTheme } from 'app/shopify';
import { IconCheckFilled, IconChevronRight } from '@tabler/icons-react-native';
import { ICON_STROKE_WIDTH, isMobileNative } from 'app/utils/constants';
const theme = createDSTheme();

const SuperPartageurScreenComponent: NavigationScreen<'super-partageur'> = ({ navigation }) => {
  const { t } = useTranslation('superPartageur');

  const alignment = useAlignmentIfDesktop();
  const { isLargeUI } = useUISize();

  const { data: user, refetch: refetchUser, isPending: isPendingUser } = useUser();
  const isSuper = user?.isSuper;

  const goToDetails = () => navigation.navigate('super-partageur-details', undefined, { pop: true });

  useNavigationOptions(
    navigation,
    {
      headerTitle: () => (
        <Text variant="bodyXLargeMedium" color="white-label.header.on-header">
          {t(`title`)}
        </Text>
      ),
    },
    false,
  );

  const setPageTitle = usePageTitle('superPartageur');
  useEffect(() => setPageTitle('title'), [setPageTitle]);

  const deviceWidth = useDeviceWidth();

  useOnFocus(() => {
    void refetchUser();
  }, true);

  useRegisterLoading(isPendingUser, 'SuperPartageurScreen - fetching user');

  return (
    <Page>
      {!isMobileNative ? (
        <Text
          fontWeight="600"
          textAlign={alignment}
          variant="headerXSmall"
          marginBottom={isLargeUI ? 'spacing.general.x8' : 'spacing.general.0'}
        >
          {t('title')}
        </Text>
      ) : null}

      <Column alignItems="center" gap="spacing.general.x4">
        <PCImage
          height={(102 / 204) * 285}
          source={isSuper ? images.badge.isSuper : images.badge.isSuperNotYet}
          width={102}
        />

        <Text textAlign="center" variant="headerXSmall">
          <Trans
            components={{ strong: <Text fontWeight="700" /> }}
            i18nKey={isSuper ? 'subtitle.isSuper' : 'subtitle.notYetSuper'}
            ns="superPartageur"
          />
        </Text>

        <SuperPartageRequirements style={{ marginTop: theme.spacing['spacing.general.x4'] }} />

        <Separator onlyWidth={isLargeUI} style={{ marginTop: theme.spacing['spacing.general.x4'] }} />

        <Responsive
          mobile={
            <Pressable onPress={goToDetails}>
              <Row alignItems="center" width="100%">
                <Text flex={1} variant="bodyBase">
                  {t('learnMore')}
                </Text>
                <IconChevronRight strokeWidth={ICON_STROKE_WIDTH} />
              </Row>
            </Pressable>
          }
        />
      </Column>

      <Responsive desktop={<SuperPartagerDetails />} />

      {isSuper ? (
        <ConfettiCannon
          colors={[theme.colors['surface.tertiary']]}
          count={100}
          fadeOut
          fallSpeed={5000}
          origin={{ x: deviceWidth, y: 0 }}
        />
      ) : null}
    </Page>
  );
};

const SuperPartageurScreenWithMetrics = withOptionalMetrics(
  'SuperPartageurScreen',
  withErrorBoundary(withDashboardLayout(SuperPartageurScreenComponent)),
);

export const SuperPartageurScreen: NavigationScreen<'super-partageur'> = (props) => (
  <SuperPartageurScreenWithMetrics {...props} />
);

interface SuperPartageRequirementParameters {
  name: string;
  currentValue?: number;
  requiredValue: number;
}

export const useIsSuperRequirements = () => {
  const { data: user } = useUser();
  const userInfo = useAtomValue(userInfoAtom);
  const statisticsQuery = useStatistics(userInfo?.id ?? '', { enabled: !!userInfo?.id });

  const requirements = statisticsQuery.data?.isSuperRequirements ?? [
    { name: 'trustPoints', requiredValue: 5 },
    { name: 'responseRate', requiredValue: 90 },
    { name: 'lastActiveDays', requiredValue: 30 },
  ];
  const filteredRequirements = isWhiteLabel(WhiteLabel.Mutuo)
    ? requirements.filter((req) => req.name !== 'trustPoints')
    : requirements;
  const requirementsDict: Record<string, number> = {};
  for (const requirement of filteredRequirements) {
    requirementsDict[requirement.name] = requirement.requiredValue;
  }

  const numTrustPoints = statisticsQuery.data?.loans.trustPoints;
  const responseRate = user?.responseRate;

  return filteredRequirements.map((req) => {
    const currentValue =
      req.name === 'trustPoints'
        ? numTrustPoints
        : req.name === 'responseRate'
          ? responseRate
            ? Math.floor(responseRate)
            : undefined
          : requirementsDict['lastActiveDays' as keyof typeof requirementsDict]; // Not a typo; just needs to be always gained
    const isCompleted = (currentValue ?? 0) >= req.requiredValue;
    return {
      ...req,
      currentValue,
      isCompleted,
    };
  });
};

export const SuperPartageRequirements = function SuperPartageRequirements({
  style,
}: {
  style?: StyleProp<ViewStyle> | undefined;
}) {
  const requirementsList = useIsSuperRequirements();

  return (
    <Column gap="spacing.general.x2" style={style} width="100%">
      {requirementsList.map((req) => (
        <SuperPartageRequirement
          currentValue={req.currentValue}
          key={req.name}
          name={req.name}
          requiredValue={req.requiredValue}
        />
      ))}
    </Column>
  );
};

const SuperPartageRequirement = function SuperPartageRequirement({
  name,
  currentValue,
  requiredValue,
}: SuperPartageRequirementParameters) {
  const { t } = useTranslation('superPartageur');

  const isObtained = (currentValue ?? 0) >= requiredValue;

  return (
    <Row
      alignItems="center"
      gap="spacing.general.x4"
      style={{
        paddingVertical: theme.spacing['spacing.general.x6'],
        paddingHorizontal: theme.spacing['spacing.general.x4'],
        backgroundColor: isObtained ? theme.colors['surface.tertiary'] : theme.colors['surface.tertiary-neutral'],
        borderRadius: theme.borderRadii['radius.lg'],
      }}
    >
      <Text fontSize={33}>{t(`requirements.${name}.emoji`)}</Text>
      <Column flex={1} gap="spacing.general.x1">
        <Text fontWeight="700" variant="bodyBase">
          {t(`requirements.${name}.title`, { value: currentValue ?? '-' })}
        </Text>
        <Text variant="bodyBase">{t(`requirements.${name}.description`, { requiredValue })}</Text>
      </Column>
      <Column
        backgroundColor={isObtained ? 'icon.brand' : 'icon.tertiary'}
        borderRadius="radius.full"
        padding="spacing.general.x1"
      >
        <IconCheckFilled color={isObtained ? theme.colors['icon.brand-alt'] : theme.colors['icon.inverse.secondary']} />
      </Column>
    </Row>
  );
};
